import type { CaptureOptionsBySource, ToggleSource } from '@livekit/components-core';
import * as React from 'react';
import { getSourceIcon } from './util.tsx';
import { useLocalParticipant, useTrackToggle } from '@livekit/components-react';
import {
  createAudioAnalyser,
  LocalAudioTrack,
  RemoteAudioTrack,
  TrackPublishOptions,
} from 'livekit-client';
import { toast } from 'sonner';
import { useCall } from '@/context/call-context/use-call.ts';
import { useGlobalAudio } from '@/context/global-audio-context/use-global-audio.ts';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { AI_ENDPOINT } from '@/config.ts';

const TRANSMIT_THRESHOLD = 0.02; // LiveKit track sending noticeable audio
const SPEAK_THRESHOLD = 0.1;

/** @public */
export interface TrackToggleProps<T extends ToggleSource>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  source: T;
  showIcon?: boolean;
  initialState?: boolean;
  onChange?: (enabled: boolean, isUserInitiated: boolean) => void;
  captureOptions?: CaptureOptionsBySource<T>;
  publishOptions?: TrackPublishOptions;
  onDeviceError?: (error: Error) => void;
}

/**
 * With the `TrackToggle` component it is possible to mute and unmute your camera and microphone.
 * The component uses an html button element under the hood so you can treat it like a button.
 *
 * WARNING: CAUSES MEMORY LEAK DUE TO PROCESSING AUDIO AND SFX
 * DONT USE WITH OTHERS MIC listeners, that access navigator.mediaDevices.getUserMedia simultaniously
 * @experimental may cause bugs, call issues due to constant sound analyzer,
 * DONT USE WITH OTHERS MIC ANALYZERS
 */
export const TrackToggleMic: <T extends ToggleSource>(
  props: TrackToggleProps<T> & React.RefAttributes<HTMLButtonElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef(function TrackToggle<
  T extends ToggleSource,
>({ showIcon, ...props }: TrackToggleProps<T>, ref: React.ForwardedRef<HTMLButtonElement>) {
  const { buttonProps, enabled, track } = useTrackToggle(props);
  const [isClient, setIsClient] = React.useState(false);
  const sfx = useGlobalSFX();
  const { activeCall } = useCall();
  const lastAlertRef = React.useRef(0);
  const { deviceId, setIsSpeaking, setVolumeLevel } = useGlobalAudio();
  const { speakingNotifications } = useSettings();
  const suppressSFXUntilRef = React.useRef<number | null>(null);
  const socket = useSocket();
  const sttWS = React.useRef<WebSocket | null>(null);
  const { localParticipant } = useLocalParticipant();

  // Use a ref to track the latest deviceId without forcing the WebSocket to reconnect
  const deviceIdRef = React.useRef(deviceId);
  React.useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  const handleSuppressSFX = () => {
    suppressSFXUntilRef.current = Date.now() + 60 * 60 * 1_000;
    toast.info('🔇 Звуковые уведомления о микрофоне отключены на 1 час');
  };

  React.useEffect(() => setIsClient(true), []);

  // --------------------------
  // STT: CONNECT WITH RECONNECT & FALLBACK
  // --------------------------
  React.useEffect(() => {
    let isMounted = true;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let aiWS: WebSocket | null = null;

    const connect = () => {
      if (!isMounted) return;

      try {
        aiWS = new WebSocket(AI_ENDPOINT);
        aiWS.binaryType = 'arraybuffer';
        sttWS.current = aiWS;

        aiWS.onopen = () => {
          console.info('[STT] Connected to AI server');
        };

        aiWS.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);

            if (data.type === 'final') {
              const finalText = data.text.trim();
              if (!finalText) return;

              const participantIdentityWithoutMetadata = localParticipant.identity.split('_')[0];

              // Получение из AI STT и отправка в tpresence для отображения в звонке
              if (socket && socket.readyState === WebSocket.OPEN && activeCall?.chatId) {
                const payload = JSON.stringify({
                  type: 'speech-to-text',
                  meta: {
                    channelId: activeCall.chatId,
                    participantIdentity: participantIdentityWithoutMetadata,
                    deviceId: deviceIdRef.current, // Use ref to prevent reconnects
                    caption: finalText,
                    timestamp: Date.now(),
                  },
                });
                socket.send(payload);
              }
            }
          } catch (err) {
            console.error('[AISTT] Failed to parse message', err);
          }
        };

        aiWS.onerror = (err) => {
          console.error('[AISTT] WebSocket error', err);
        };

        aiWS.onclose = (event) => {
          console.warn(
            `[AISTT] WebSocket closed (code: ${event.code}, reason: ${event.reason || 'No reason'})`
          );
          sttWS.current = null;

          if (isMounted && event.code !== 1_000) {
            console.info('[STT] Reconnecting in 3 seconds...');
            reconnectTimeout = setTimeout(connect, 3_000);
          }
        };
      } catch (err) {
        console.error('[STT] Failed to create WebSocket', err);
        if (isMounted) {
          reconnectTimeout = setTimeout(connect, 5_000);
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (aiWS) {
        aiWS.onclose = null;
        if (aiWS.readyState === WebSocket.OPEN || aiWS.readyState === WebSocket.CONNECTING) {
          aiWS.close(1_000, 'Component unmounted');
        }
      }
      sttWS.current = null;
    };
  }, [activeCall?.chatId, localParticipant?.identity, socket]); // Removed deviceId to avoid 3s reconnect delay when switching mics

  // --------------------------
  // STT HELPERS
  // --------------------------
  function convertFloat32ToPCM16(float32: Float32Array) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      let s = Math.max(-1, Math.min(1, float32[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }

  function resampleBuffer(buffer: Float32Array, inputRate: number, targetRate = 16000) {
    if (inputRate === targetRate) return buffer;
    const ratio = inputRate / targetRate;
    const newLen = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLen);

    for (let i = 0; i < newLen; i++) {
      const exactIndex = i * ratio;
      const index = Math.floor(exactIndex);
      const fraction = exactIndex - index;

      if (index + 1 < buffer.length) {
        result[i] = buffer[index] + fraction * (buffer[index + 1] - buffer[index]);
      } else {
        result[i] = buffer[index];
      }
    }
    return result;
  }

  React.useEffect(() => {
    let localAnalyserCleanup: (() => void) | undefined;
    let interval: NodeJS.Timeout;
    let sttCtx: AudioContext | null = null;
    let sttProcessor: ScriptProcessorNode | null = null;
    let localStream: MediaStream | null = null; // Отдельный поток для локального VAD
    let sttStream: MediaStream | null = null; // Поток для STT (переиспользует LiveKit)
    let localCtx: AudioContext | null = null;
    let getLocalVolume = () => 0;

    async function setup() {
      if (!track?.audioTrack) return;

      const audioTrack = track.audioTrack as LocalAudioTrack | RemoteAudioTrack;
      const { calculateVolume, cleanup } = createAudioAnalyser(audioTrack);
      localAnalyserCleanup = cleanup;

      try {
        // Локальный VAD (детект "Вы говорите, а микрофон выключен")
        // Нам нужен ОТДЕЛЬНЫЙ поток с микрофона. Когда пользователь мьютит
        // микрофон в Talksy KIT (enabled = false), трек Talksy KIT глохнет.
        // А нам нужно продолжать слушать микрофон, чтобы показывать уведомление.
        try {
          localStream = await navigator.mediaDevices.getUserMedia({
            audio: deviceId ? { deviceId: { exact: deviceId } } : true,
          });

          localCtx = new AudioContext();
          if (localCtx.state === 'suspended') await localCtx.resume();

          const localSource = localCtx.createMediaStreamSource(localStream);
          const analyser = localCtx.createAnalyser();
          analyser.fftSize = 256;
          localSource.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);

          getLocalVolume = () => {
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((sum, v) => sum + v, 0) / data.length;
            return avg / 100;
          };
        } catch (err) {
          console.warn('Local mic access failed (VAD disabled)', err);
        }

        //  STT (Распознавание речи)
        if ('mediaStreamTrack' in audioTrack && audioTrack.mediaStreamTrack) {
          sttStream = new MediaStream([audioTrack.mediaStreamTrack]);
          console.info('[STT] Reusing TALKSY KIT track for AI STT to avoid hardware locking');
        } else if (localStream) {
          sttStream = localStream; // Fallback, если трек Talksy KIT не удалось достать
        } else {
          console.warn('[STT] No audio stream available for STT');
          return;
        }

        sttCtx = new AudioContext({ sampleRate: 48_000 });
        if (sttCtx.state === 'suspended') await sttCtx.resume();

        const sttSource = sttCtx.createMediaStreamSource(sttStream);
        sttProcessor = sttCtx.createScriptProcessor(4096, 1, 1);

        sttProcessor.onaudioprocess = (event) => {
          if (!enabled) return;
          const currentWs = sttWS.current;
          if (!currentWs || sttWS?.current?.readyState !== 1) return;

          try {
            const input = event.inputBuffer.getChannelData(0);
            const resampled = resampleBuffer(input, sttCtx!.sampleRate, 16_000);
            const pcm16 = convertFloat32ToPCM16(resampled);
            currentWs.send(pcm16.buffer);
          } catch (err) {
            // ignore
          }
        };

        sttSource.connect(sttProcessor);
        if (sttCtx.destination) {
          sttProcessor.connect(sttCtx.destination);
        }

        // Интервал проверки уведомлений
        interval = setInterval(() => {
          const now = Date.now();
          const inCall = !!(activeCall && activeCall?.chatId);

          const transmittedVolume = calculateVolume();
          const localVolume = getLocalVolume();
          setVolumeLevel(localVolume);
          const speaking = localVolume > SPEAK_THRESHOLD;
          setIsSpeaking(speaking);
          const micActive = enabled;
          const isMicTransmitting = micActive && transmittedVolume > TRANSMIT_THRESHOLD;

          if (inCall && isMicTransmitting) {
            lastAlertRef.current = now;
            return;
          }

          // Проверка "Вы говорите, а микрофон выключен"
          // localVolume > 0, потому что localStream НЕ замьючен, даже если enabled === false
          if (inCall && speaking && !micActive && transmittedVolume < TRANSMIT_THRESHOLD) {
            if (now - lastAlertRef.current > 5_000) {
              lastAlertRef.current = now;
              if (!speakingNotifications) return;

              if (!suppressSFXUntilRef.current || Date.now() > suppressSFXUntilRef.current) {
                toast.info('🎙️ Вы говорите, а микрофон выключен', {
                  action: {
                    label: 'Не оповещать (1 час)',
                    onClick: () => {
                      handleSuppressSFX();
                    },
                  },
                });
                sfx.speakingWithMute.play();
              } else {
                toast.info('🎙️ Вы говорите, а микрофон выключен', {
                  description: 'Включите микрофон, чтобы вас было слышно.',
                });
              }
            }
          }
        }, 300);
      } catch (err) {
        console.warn('Microphone setup failed for analyser or STT', err);
      }
    }

    setup();

    return () => {
      if (interval) clearInterval(interval);
      localAnalyserCleanup?.();

      // Останавливаем только локальный поток (VAD)
      // Поток STT использует трек LiveKit, его останавливать нельзя, иначе отвалится звонок!
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }

      try {
        sttProcessor?.disconnect();
        sttCtx?.close();
        localCtx?.close();
      } catch {}
    };
  }, [track?.audioTrack, enabled, deviceId, activeCall?.chatId, speakingNotifications]);

  return (
    isClient && (
      <button ref={ref} {...buttonProps}>
        {(showIcon ?? true) && getSourceIcon(props.source, enabled)}
        {props.children}
      </button>
    )
  );
});
